import { SerializedStyles } from "@emotion/react";
import { ReactNode, Ref, memo, useCallback, useEffect } from "react";
import { usePartialSubmit } from "src/apps/AppFlow/pages/hooks";
import { BooleanRadioOptions, FormFlowPage } from "src/components";
import { createStyles } from "src/styles";
import { Applicant } from "src/types/Applicant";
import { WaCivilUnionAddendumValue } from "src/types/Application";

import { formatters } from "@fraction/shared";

import { ConnectedToDropdown } from "./components";

export interface WaCivilUnionAddendumProps {
  onChange: (value: WaCivilUnionAddendumValue) => void;
  value?: WaCivilUnionAddendumValue;
  firstName?: string;
  otherApplicants?: Applicant[];

  focusRef?: Ref<HTMLInputElement>;
  onNext: () => void;
  onBack: () => void;
  style?: SerializedStyles;
  children?: ReactNode;
}

const styles = createStyles({
  radioOptions: { marginBottom: 24 },
});
const TITLE = "Marital status (continued)";

const WaCivilUnionAddendum = ({
  firstName,
  otherApplicants,
  onChange,
  value,
  focusRef,
  onNext,
  onBack,
  style,
  children,
  ...props
}: WaCivilUnionAddendumProps) => {
  // Custom validation / handleNext logic for martial status as it's a bit tricky. E.g. we conditionally use 2
  // types on inputs (a dropdown _and_ a free-form text input) for the connectedToName value which doesn't
  // work well with react-hook-forms (you can only register to one input).
  const connectedToNameEnabled = value?.waCivilUnionAddendum === true;
  const connectedToNameIsValid = !connectedToNameEnabled || (value?.connectedToName || "").length >= 5;
  const validate = useCallback(
    () => value?.waCivilUnionAddendum !== undefined && connectedToNameIsValid,
    [value, connectedToNameIsValid]
  );
  const { handleNext, nextDisabled, nextLoading } = usePartialSubmit({ onNext, validate });

  const moreThanOneApplicant = !!otherApplicants?.length;
  const connectedToEnabled = value?.waCivilUnionAddendum === true;
  const possessiveFirstName = formatters.user.possessive(firstName);
  const description = moreThanOneApplicant
    ? `Is ${firstName} in a Civil Union or a Domestic Partnership, or do they have a non-borrowing spouse, or are they a party to a Designated Beneficiary Agreement?`
    : "Are you in a Civil Union or a Domestic Partnership, or do you have a non-borrowing spouse, or " +
      "are you a party to a Designated Beneficiary Agreement?";

  const handleChangeWaCivilUnion = useCallback(
    (waCivilUnionAddendum: boolean) => {
      onChange({ ...value, waCivilUnionAddendum });
    },
    [value, onChange]
  );

  useEffect(() => {
    if (!connectedToEnabled && value?.connectedToName !== undefined) {
      onChange({ ...value, connectedToName: undefined });
    }
  }, [value, onChange, connectedToEnabled]);

  return (
    <FormFlowPage
      {...props}
      title={TITLE}
      description={description}
      onNext={handleNext}
      onBack={onBack}
      style={style}
      nextDisabled={nextDisabled}
      nextLoading={nextLoading}
    >
      <BooleanRadioOptions
        focusRef={focusRef}
        value={value?.waCivilUnionAddendum}
        onChange={handleChangeWaCivilUnion}
        style={styles.radioOptions}
      />
      {connectedToEnabled && (
        <ConnectedToDropdown
          onChange={onChange}
          value={value}
          otherApplicants={otherApplicants}
          possessiveFirstName={possessiveFirstName}
          hasConnectedToNameError={connectedToNameIsValid}
        />
      )}
      {children}
    </FormFlowPage>
  );
};

export default memo(WaCivilUnionAddendum);
